import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Uuid } from '@/data/db';
import { TaskDetailDrawer } from '@/features/tasks/task-detail-drawer';

interface DetailDrawerContextValue {
  openTask: (taskId: Uuid) => void;
  close: () => void;
  openTaskId: Uuid | null;
}

const DetailDrawerContext = createContext<DetailDrawerContextValue | null>(null);

/** 全域的詳細抽屜 host（plan.md Phase 3）；任何頁面都能開啟同一個抽屜。 */
export function DetailDrawerProvider({ children }: { children: ReactNode }) {
  const [openTaskId, setOpenTaskId] = useState<Uuid | null>(null);

  const openTask = useCallback((taskId: Uuid) => setOpenTaskId(taskId), []);
  const close = useCallback(() => setOpenTaskId(null), []);

  const value = useMemo<DetailDrawerContextValue>(
    () => ({ openTask, close, openTaskId }),
    [openTask, close, openTaskId],
  );

  return (
    <DetailDrawerContext value={value}>
      {children}
      <TaskDetailDrawer
        taskId={openTaskId}
        onOpenChange={(open) => {
          if (!open) close();
        }}
        onOpenTask={openTask}
      />
    </DetailDrawerContext>
  );
}

export function useDetailDrawer(): DetailDrawerContextValue {
  const context = useContext(DetailDrawerContext);
  if (!context) throw new Error('useDetailDrawer 必須放在 DetailDrawerProvider 之內');
  return context;
}

/** 頁面在沒有 host 的測試環境下也要能渲染。 */
export function useOptionalDetailDrawer(): DetailDrawerContextValue | null {
  return useContext(DetailDrawerContext);
}
